import * as React from 'react';
import { connect } from 'react-redux';
import { SET_UBUILDER_LITE, CLEAR_UBUILDER_LITE, MARK_LITE_DIRTY, CLEAR_LITE_DIRTY, CLEAR_LITE_DIRTY_SUPPRESSION } from 'app/appActions';
import { BOM_RESET, LOAD_PROJECT_CONFIGURATION, SET_PRODUCT_ID, SET_PRODUCT_TITLE, UPDATE_LITE_PROJECT_REQUEST, SET_PROJECT_OPTION, CLEAR_DATA_TO_LOAD, CLEAR_PROJECT_CONFIGURATION, CLEAR_OBSTRUCTIONS } from 'actions';
import { FETCH_LITE_BOM_REQUEST } from 'v3/dashboard/liteProjectActions';
import { getProductName, isCommercialProduct, isNxtHorizon, isSolarMount, products } from '__common/constants/products';
import { ACTIVE_PANELS_EDITOR, DEACTIVE_PANELS_EDITOR, SAVE_BLANK_MAP_ROOF_LENGTH_WIDTH, SET_METERS_PER_PIXEL } from '__editor/panelsEditor/components/background/backgroundActions';
import { LOAD_ROOFS_FROM_API } from '__editor/googleMapsRoofsSelector/components/drawingManager/drawingManagerActions';
import { setProductSettings } from '__editor/panelsEditor/components/productsSettings/productsSettings';
import { getDefaultProjectConfiguration } from 'projectDesign/components/projectConfiguration/utils/defaultProps';
import { getDefaultRoofPitchValue } from '__editor/panelsEditor/components/tiltedRoof/tiltedRoof';
import { loadDefaultModule } from '__common/components/moduleSelector/moduleSelector';
import { getProductTitle } from '__common/constants/products_titles';
import { loadProject, loadProjectData } from '__editor/components/roofsSelector/components/roofsSelectorSaveLoadProject/loadProject';
import { deactivePanelsEditor } from '__editor/panelsEditor/panelsEditorHelper';
import { launchRecheckPanelExposure } from '__editor/panelsEditor/components/panels/utils/exposureRecheck';
import { SET_BLANK_MAPS_ROOFS_SELECTOR, SET_DEFAULT_MAP_ROOFS_SELECTOR, SET_MAP_ROOFS_SELECTOR } from '__editor/components/roofsSelector/roofsSelectorActions';
import { SET_DRAW_RUBBER_BAND } from '__editor/panelsEditor/actions';
import { SHOW_CURSOR, SET_PORTRAIT_CURSOR } from '__editor/panelsEditor/components/cursor/cursorActions';
import { UNBLOCK_CREATING_PANEL } from '__editor/panelsEditor/components/panels/panelsActions';
import { SELECT_ROOF_LEAD_EDGE } from '__editor/googleMapsRoofsSelector/components/leadEdgeRoofSelector/leadEdgeRoofSelectorActions';
import { findSouthMostEdgeIndex } from '__editor/googleMapsRoofsSelector/components/leadEdgeRoofSelector/utils/selectEdge';
import { savePanelsEditor } from '__editor/panelsEditor/components/panels/panels';
import { LITE_SEED_HISTORY } from 'v3/workspace/history/liteHistoryActions';
import { ROOF_PITCH } from '__editor/panelsEditor/components/tiltedRoof/roofPitch';
import { SET_TILTED_ROOF } from '__editor/panelsEditor/components/tiltedRoof/tiltedRoofActions';
import { MAP_TYPE } from '__common/constants/map';

// Reverse of ROOF_PITCH ({ "3/12": 14 } → { 14: "3/12" }). Backend persists
// pitch as the integer; dropdown options use the "N/12" labels.
const ROOF_PITCH_INT_TO_LABEL: { [k: number]: string } = Object.keys(ROOF_PITCH).reduce(
  (acc, label) => { acc[(ROOF_PITCH as any)[label]] = label; return acc; },
  {} as { [k: number]: string },
);
import { state as getState } from '__common/store';
import { checkModelData } from '__common/components/Drawer/utils/checkModelData';
import DesignWorkspace from 'v3/workspace/DesignWorkspace';

const DEFAULT_CORDS = { lat: 35.0844, lng: -106.6504 };
const DEFAULT_ROOF_EDGES = [
  { lat: 35.0846, lng: -106.6506 },
  { lat: 35.0846, lng: -106.6502 },
  { lat: 35.0842, lng: -106.6502 },
  { lat: 35.0842, lng: -106.6506 },
];

const AUTO_SAVE_INTERVAL_MS = 1000;

// Debounce applied to a dirty-driven autosave (componentDidUpdate → became
// dirty). Kept well under AUTO_SAVE_INTERVAL_MS so a settled edit persists —
// and chains the BOM refetch — quickly, matching Live BOM's near-immediate
// refetch instead of waiting for the next 1s poll tick. The 1s interval stays
// as a safety net for any dirtying that doesn't re-render the container.
const AUTO_SAVE_DEBOUNCE_MS = 300;

// Short defer handed to launchRecheckPanelExposure from the autosave path. The
// idle gate below already guarantees the user has paused, so we don't need the
// shared 1000ms defer — that extra second only delayed the PUT (and therefore
// the BOM refetch) behind Live BOM.
const LITE_EXPOSURE_DEFER_MS = 0;

// Project-wide panel count drawn from Redux. Active roof's panels live in
// `panels.panels`; other roofs' counts sit on `drawingManager.roofs[id].panels`.
// Used to gate the initial BOM GET (skip when zero panels — fresh projects and
// post-clear-all states show empty-state placeholders instead).
export function countLitePanels(s: any): number {
  const roofs = (s && s.drawingManager && s.drawingManager.roofs) || {};
  const selectedRoofId = s && s.background && s.background.selectedRoofId;
  const activePanels = (s && s.panels && s.panels.panels) || [];
  let total = Array.isArray(activePanels) ? activePanels.length : 0;
  Object.keys(roofs).forEach((roofId) => {
    if (String(roofId) === String(selectedRoofId)) return;
    const rp = roofs[roofId] && roofs[roofId].panels;
    if (Array.isArray(rp)) total += rp.length;
  });
  return total;
}

type BootstrapPhase = 'fetching' | 'loading' | 'seeding' | 'ready';

type Props = {
  dispatch: Function;
  projectName: string;
  user: { email?: string; username?: string };
  liteUpdating: boolean;
  liteDirty: boolean;
  match: {
    params: {
      productId: string;
      projectId?: string;
    };
  };
  modelData: any;
  cords: any;
  saveLoadProject: saveLoadProjectState;
  isLoading: boolean;
};

type State = {
  phase: BootstrapPhase;
};

// Local boundary so a PanelsEditor crash does NOT escape to ReduxRouter,
// which would unmount UBuilderLiteContainer and re-trigger loadProject →
// crash → unmount → load loop. Keeping the container mounted lets us show
// the actual error and stops the bootstrap cascade.
class LitePanelsEditorBoundary extends React.Component<any, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: any) {
    // eslint-disable-next-line no-console
    console.error('[LitePanelsEditorBoundary] caught:', error, info && info.componentStack);
  }

  render() {
    const err = this.state.error;
    if (err) {
      return (
        <div style={{ padding: 24, fontFamily: 'monospace', color: '#b00020' }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Panel editor failed to render.</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{String(err.stack || err.message)}</div>
          <div style={{ marginTop: 8, color: '#444' }}>The bootstrap loop has been stopped. Reload the page to retry.</div>
        </div>
      );
    }
    return (this as any).props.children;
  }
}

class UBuilderLiteContainer extends React.Component<Props, State> {
  state: State = { phase: 'fetching' };
  private autoSaveInterval: ReturnType<typeof setInterval> | null = null;
  private autoSaveTimeout: ReturnType<typeof setTimeout> | null = null;
  // Captured from the GET response before loadProject clears dataToLoad. The
  // legacy env loaders strip project-level roof_pitch, so we stash it here and
  // inject it into the seeded default roof during advanceToSeeding.
  private savedRoofPitchLabel: string | null = null;
  // Idle-gate timestamp: maybeAutoSave defers its autosave tick when the user is
  // mid-rapid-add. Synchronous savePanelsEditor + RECHECK_PANELS_EXPO bursts
  // would otherwise block the main thread mid-click and delay panel adds.
  private lastInteractionAt: number = 0;
  private trackInteraction = () => {
    this.lastInteractionAt = Date.now();
    // End the load-time dirty-suppression window on the first real user
    // interaction so genuine edits (panels, fields, module) dirty and autosave.
    if (!this.dirtySuppressionCleared) {
      this.dirtySuppressionCleared = true;
      this.props.dispatch(CLEAR_LITE_DIRTY_SUPPRESSION());
    }
  };
  private dirtySuppressionCleared: boolean = false;

  private scheduleAutoSave = (delayMs: number = AUTO_SAVE_DEBOUNCE_MS) => {
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout);
      this.autoSaveTimeout = null;
    }
    this.autoSaveTimeout = setTimeout(() => {
      this.autoSaveTimeout = null;
      this.maybeAutoSave();
    }, delayMs);
  };

  private extractSavedPitch = (data: any) => {
    if (!data || !data.project_configuration) return;
    const savedPitchInt = typeof data.project_configuration.roof_pitch === 'number'
      ? data.project_configuration.roof_pitch
      : null;
    this.savedRoofPitchLabel = savedPitchInt !== null
      ? (ROOF_PITCH_INT_TO_LABEL[savedPitchInt] || String(savedPitchInt))
      : null;
  };

  private getFallBackPitch = () => {
    const { match: { params: { productId } } } = this.props;
    const numericProductId = products[productId];
    // Fall back to product-specific defaults if no saved value is available
    // (e.g. fresh projects). RM10 Evo/GF/etc typically default to '1' or '0'
    // whereas residential rail systems (SM/NXT) default to '3/12'.
    return getDefaultRoofPitchValue(numericProductId, 1, 1) || '14';
  };

  componentDidMount() {
    const { dispatch, match: { params: { productId, projectId } } } = this.props;
    const numericProductId = products[productId];

    // Hard reset every editor slice that might leak from a previous session.
    // This guarantees create/open/refresh all start from the same baseline and
    // the phase machine below can rely on state transitions, not prop history.
    dispatch(DEACTIVE_PANELS_EDITOR());
    dispatch(LOAD_ROOFS_FROM_API({}));
    // state.bom is shared with legacy and otherwise survives the dashboard
    // → workspace remount, so opening a second project shows the previous
    // project's parts/accessories/totals until the first autosave's
    // FETCH_LITE_BOM_REQUEST returns. Clear it here for a clean slate.
    const currentBomProjectId = (getState() as any).bom.projectId;
    console.log(currentBomProjectId, projectId);
    if (currentBomProjectId !== projectId) {
      dispatch(BOM_RESET());

    }
    dispatch(SET_UBUILDER_LITE(true));
    dispatch(SET_PRODUCT_ID(numericProductId));
    dispatch(SET_PRODUCT_TITLE(getProductTitle(numericProductId, false)));

    if (isNxtHorizon(numericProductId) || isSolarMount(numericProductId)) {
      dispatch(SET_PORTRAIT_CURSOR());
    }

    if (!projectId) {
      const isCommercialDefault = isCommercialProduct(numericProductId);
      setProductSettings(numericProductId, isCommercialDefault ? 'google' : 'white', false, '');
      dispatch(isCommercialDefault ? SET_DEFAULT_MAP_ROOFS_SELECTOR() : SET_BLANK_MAPS_ROOFS_SELECTOR());
    }

    if (projectId) {
      // Always GET the project — create flow already dispatched FETCH_PROJECT
      // via FETCH_PROJECT_AFTER_LITE_CREATE, so this no-ops in that case.
      const { saveLoadProject: { dataToLoad } } = this.props;
      console.log(dataToLoad);
      if (dataToLoad && dataToLoad.project_configuration) {
        this.extractSavedPitch(dataToLoad);
        loadProject(dataToLoad);
      } else {
        loadProjectData(productId, projectId);
      }



    } else {
      const appState = getState() as any;
      const existingEnv = appState.projectConfiguration && appState.projectConfiguration.projectEnvConfig;
      if (!existingEnv || !existingEnv.manufacturer_name) {
        const defaultConfig = getDefaultProjectConfiguration(numericProductId);
        dispatch(LOAD_PROJECT_CONFIGURATION(defaultConfig, numericProductId));
        loadDefaultModule(productId);
      }
    }

    this.autoSaveInterval = setInterval(this.maybeAutoSave, AUTO_SAVE_INTERVAL_MS);
    document.addEventListener('pointerdown', this.trackInteraction, true);
    // Also catch keyboard-only field edits (typing in a left-panel input) so the
    // load-time dirty suppression is lifted even without a pointer interaction.
    document.addEventListener('keydown', this.trackInteraction, true);
  }

  componentDidUpdate(prevProps: Props) {
    const { phase } = this.state;
    if (phase === 'ready') {
      const becameDirty = !prevProps.liteDirty && this.props.liteDirty;
      const saveFinishedStillDirty = prevProps.liteUpdating && !this.props.liteUpdating && this.props.liteDirty;
      if (becameDirty || saveFinishedStillDirty) {
        this.scheduleAutoSave();
      }
      return;
    }

    const { saveLoadProject, match: { params: { projectId } } } = this.props;

    // Phase 1 → 2: GET responded. Run loadProject(data) exactly once.
    if (phase === 'fetching') {
      if (!projectId) {
        // No-project-id path (direct navigation to /lite/design/<product>).
        // Nothing to load; jump straight to seeding once module defaults arrive.
        if (checkModelData()) this.advanceToSeeding();
        return;
      }
      const data = saveLoadProject.dataToLoad;
      if (saveLoadProject.isLoading) return;
      if (!data || !data.project_configuration) return;
      // Stash project-level roof_pitch from the raw GET before loadProject
      // dispatches CLEAR_DATA_TO_LOAD. Legacy env loaders drop the field, so
      // this is the only window where the saved value is available to Lite.
      this.extractSavedPitch(data);
      this.setState({ phase: 'loading' });
      // Wrap in try/catch: legacy loadProject can throw on edge cases in
      // lite-saved data; bubbling would trip the ReduxRouter error boundary
      // and remount this container in a loop.
      try {
        loadProject(data);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[UBuilderLiteContainer] loadProject threw, continuing:', err);
      }
      // Restore the saved view. Two signals (in order of trust):
      //   1. project_configuration.map_type (1=satellite) — what we wrote out.
      //   2. Any loaded roof with id !== 0 — satellite mode is the only path
      //      that mints non-zero ids, so this catches projects that pre-date
      //      the map_type field or that round-tripped through a backend that
      //      stripped it. Without this, an old satellite save reopens as
      //      blank canvas with the marked roofs invisibly stranded in redux.
      // String/number coercion: backend's IntegerField round-trips as a
      // number, but JSON parsers and some clients stringify — accept both.
      const savedMapType = data.project_configuration && data.project_configuration.map_type;
      const loadedRoofs = data.roofs || [];
      const hasNonZeroRoof = Array.isArray(loadedRoofs)
        && loadedRoofs.some((r: any) => r && r.id !== undefined && r.id !== null && Number(r.id) !== 1);
      const routeProductSlug = this.props.match.params.productId;
      const routeNumericPid = routeProductSlug ? products[routeProductSlug] : undefined;
      const isCommercialProject = routeNumericPid && isCommercialProduct(routeNumericPid);

      const isSatelliteSave = savedMapType !== undefined && savedMapType !== null
        ? (savedMapType === 1 || savedMapType === '1')
        : (hasNonZeroRoof || isCommercialProject);
      this.props.dispatch(isSatelliteSave
        ? SET_DEFAULT_MAP_ROOFS_SELECTOR()
        : SET_BLANK_MAPS_ROOFS_SELECTOR());
      return;
    }

    // Phase 2 → 3: modelData populated by loadProject's setPanelsModel +
    // FETCH_MFGS_AND_MODELS_REQUEST chain. Seed default roof and activate.
    if (phase === 'loading') {
      // setPanelsModel's SYNCHRONOUS SET_MODEL uses a customModel that already
      // satisfies checkModelData(), so we can go 'ready' as soon as it lands —
      // no need to block the editor waiting on the (sometimes slow) models API.
      // Load-time dispatches that resolve later (async models fill-in, panels-
      // editor hydration spacing/resizes) would otherwise dirty the freshly
      // loaded project and make the 5s autosave PUT it back. liteSuppressDirty
      // (set on entering Lite, cleared on the user's first interaction) makes the
      // middleware ignore all such load-path mutations, so they never trigger a
      // spurious save.
      if (!checkModelData()) return;
      this.advanceToSeeding();
      return;
    }
  }

  advanceToSeeding = () => {
    this.setState({ phase: 'seeding' });

    const appState = getState() as any;
    const roofs = (appState.drawingManager && appState.drawingManager.roofs) || {};
    const hasServerRoofs = Object.keys(roofs).length > 0;
    // Satellite-saved projects: skip seeding the default canvas roof and skip
    // activating it. The user will see the satellite map with their marked
    // roofs preserved (via map.fitBounds + drawingManager.roofs rehydration).
    // Activating a non-existent roof here was the source of the
    // `roofs[selectedRoofId].roofPitch` crash on reopen.
    const isSatelliteProject = appState.roofsSelector && appState.roofsSelector.mapType !== MAP_TYPE.BLANK;

    // Canvas projects: ensure roofs[1] (the seeded default) exists. Drop
    // any satellite-shaped roofs (id >= 2) that may have leaked in from a
    // previous session before persistence was wired up — they'd otherwise
    // render unwanted panels in canvas. Satellite projects: keep server
    // roofs as-is; rehydrate paints them on the live Google map.
    if (!isSatelliteProject) {
      const seedFromServer = roofs && roofs[1];
      const hasStraySatelliteRoofs = hasServerRoofs && Object.keys(roofs).some((k) => Number(k) !== 1);
      if (!seedFromServer || hasStraySatelliteRoofs) {
        const defaultRoof = seedFromServer || {
          // id MUST be set: parseRoofs serialises `id: roof.id` into the PUT
          // body and getRoofs.reduce keys the loaded map by `roof.id`.
          id: 1,
          roofPitch: this.savedRoofPitchLabel || this.getFallBackPitch(),
          structureTilt: null,
          tableRowSpacing: undefined,
          panels: null,
          metersPerPixel: 0.1,
          marker: DEFAULT_CORDS,
          coords: DEFAULT_ROOF_EDGES,
          roofName: 'Roof 1',
          bgOffset: { x: 0, y: 0 },
          bgRotation: 0,
          skewEdgesPixiCords: null,
          panelsRotationDegrees: 0,
          lowEdgeToRoof: 50,
          exposureRecaluclationHash: null,
          panelBayMapping: new Map(),
          isConfirmedYesorNo: false,
        };
        // Replace the entire roofs map with just id=1. This wipes any stray
        // satellite-shaped roofs (id >= 2) that would otherwise render
        // unwanted panels in canvas mode.
        this.props.dispatch(LOAD_ROOFS_FROM_API({ 1: defaultRoof }));
      }
    }

    // Lite skips the lead-edge UI step. Mirror what the legacy flow does
    // (dispatch SELECT_ROOF_LEAD_EDGE on roof creation) so the slice stays
    // populated for the save payload and any consumer that reads it without
    // its own undefined-fallback.
    if (!isSatelliteProject) {
      const southMostIndex = findSouthMostEdgeIndex(DEFAULT_ROOF_EDGES);
      this.props.dispatch(SELECT_ROOF_LEAD_EDGE(southMostIndex, 1));
      this.props.dispatch(SET_METERS_PER_PIXEL(0.1));
    }

    // Keep state.tiltedRoof.roofPitch in sync with the seeded per-roof value.
    // savePanelsEditor() reads from tiltedRoof and overwrites the per-roof
    // copy on every flush (drawingManagerReducer.ts:115); without this sync,
    // the first auto-save tick after load would clobber a saved pitch back
    // to whatever tiltedRoof had (typically the SET_TILTED_ROOF default).
    const seededPitch = this.savedRoofPitchLabel || this.getFallBackPitch();
    this.props.dispatch(SET_TILTED_ROOF(seededPitch));

    // Satellite projects: leave selectedRoofId null so DesignWorkspace
    // shows the satellite map. The user clicks `+` on a marked roof to
    // enter the per-roof editor; that path dispatches ACTIVE_PANELS_EDITOR.
    // Canvas projects: activate the seed roof (id=1) which always exists
    // because we just created it above when hasServerRoofs was false.
    if (!isSatelliteProject) {
      this.props.dispatch(ACTIVE_PANELS_EDITOR(DEFAULT_CORDS, DEFAULT_ROOF_EDGES, 1));
    }

    // RM10 EVO on blank map: FogOfWar shows grey overlay when
    // blank_map_building_length or blank_map_building_width is 0.
    // Seed from the project's building_length / shortest_building_length
    // so the editor is usable immediately after creation.
    // productId lives on projectConfiguration (not projectEnvConfig).
    const pcNow = (getState() as any).projectConfiguration || {};
    const envNow = pcNow.projectEnvConfig || {};
    const isCommercialFlat = isCommercialProduct(pcNow.productId);
    if (!isSatelliteProject && isCommercialFlat) {
      const bLen = Number(envNow.building_length) || 20;
      const sLen = Number(envNow.shortest_building_length) || 20;
      this.props.dispatch(SAVE_BLANK_MAP_ROOF_LENGTH_WIDTH(bLen, sLen, 0.1));
    }
    // Restore roof_pitch from per-roof liteRoofPitch. The backend's GET
    // doesn't return roof_pitch in project_configuration for commercial
    // products, so we stash it in the roof JSON and re-inject into env here.
    if (isCommercialFlat && hasServerRoofs) {
      const firstRoofKey = Object.keys(roofs)[0];
      const savedPitch = roofs[firstRoofKey] && roofs[firstRoofKey].liteRoofPitch;
      if (typeof savedPitch === 'number' && savedPitch > 0) {
        this.props.dispatch(SET_PROJECT_OPTION('roof_pitch', savedPitch));
      }
    }

    setTimeout(() => {
      this.props.dispatch(SET_DRAW_RUBBER_BAND());
      this.props.dispatch(UNBLOCK_CREATING_PANEL());
      this.props.dispatch(SHOW_CURSOR());
    }, 100);

    // Force one autosave after seeding so a fresh project (or a reload that
    // re-injects the default roof) persists its baseline. The middleware's
    // whitelist excludes the bootstrap dispatches above, so without this
    // explicit mark the first 5s tick would skip and the seeded state would
    // never reach the server until the user touched something.
    // this.props.dispatch(MARK_LITE_DIRTY());

    // Seed the lite undo history with the panels that just finished
    // loading. Without this anchor, the reducer's initial empty snapshot
    // sits at the bottom of the stack and undoing past the user's
    // session-edits walks back into "no panels" — even for refreshed
    // projects that already had committed panels. Defer past the
    // panelsEditor mount + panelsManagment.loadPanels chain so we capture
    // the panels in their final post-load shape.
    setTimeout(() => {
      const s = getState() as any;
      const panels = (s.panels && s.panels.panels) || [];
      this.props.dispatch(LITE_SEED_HISTORY(panels));
    }, 250);

    // Wipe any liteDirty set by load-time dispatches (setPanelsModel's
    // SET_MFG/SET_MODEL and the now-settled models API chain, all whitelisted in
    // liteDirtyMiddleware). We only reach here once the models fetch resolved
    // (see the 'loading' gate), so no async dirtying remains in flight. This
    // guarantees the first autosave tick after load is a no-op unless the user
    // actually edits something — preventing the spurious PUT → BOM-fetch chain.
    this.props.dispatch(CLEAR_LITE_DIRTY());

    this.setState({ phase: 'ready' });

    // Fire an initial BOM fetch as soon as we have a projectId. Without this,
    // the BOM_RESET in componentDidMount leaves state.bom.partsList empty
    // until the first 5s autosave PUT succeeds and chains a fetch — which
    // means the right-panel Attachment section (and any other BOM row) is
    // blank for up to 5 seconds after every refresh. Lite's BOM is fully
    // derivable from the persisted project state, so a GET on mount is safe.
    // Always fetch — empty projects get an empty BOM which lets bomReady flip
    // true so the panel shows its empty-state placeholders instead of the
    // loading spinner.
    const { projectId } = this.props.match.params;
    const numericProductId = this.props.match.params.productId
      ? products[this.props.match.params.productId]
      : undefined;
    const productName = numericProductId ? getProductName(numericProductId) : '';
    if (projectId && productName) {
      this.props.dispatch(FETCH_LITE_BOM_REQUEST({ projectId, productId: productName }));
    }
  }

  componentWillUnmount() {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
      this.autoSaveInterval = null;
    }
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout);
      this.autoSaveTimeout = null;
    }
    document.removeEventListener('pointerdown', this.trackInteraction, true);
    document.removeEventListener('keydown', this.trackInteraction, true);
    // Only flush an auto-save if we actually finished bootstrapping. Calling
    // it during an error-driven unmount drags savePanelsEditor() through
    // half-initialised state and pollutes the next mount.
    if (this.state.phase === 'ready') {
      this.maybeAutoSave({ force: true });
    }

    try {
      deactivePanelsEditor();
    } catch (e) { /* roofs[selectedRoofId] may be undefined during load */ }

    // deactivePanelsEditor only dispatches DEACTIVE_PANELS_EDITOR inside the
    // hasRoofObstructionHeightSet() branch, which is never true for Lite. Force
    // the reset so the next mount's phase machine starts from a clean slate.
    this.props.dispatch(DEACTIVE_PANELS_EDITOR());
    this.props.dispatch(LOAD_ROOFS_FROM_API({}));
    this.props.dispatch(CLEAR_DATA_TO_LOAD());
    this.props.dispatch(CLEAR_UBUILDER_LITE());
    // Wipe the per-project design state so the next project opened in this tab
    // (new OR existing) starts clean. The force-save above already captured its
    // payload synchronously (the UPDATE_LITE_PROJECT_REQUEST epic reads state()
    // inline), so clearing here cannot clobber the in-flight save. Without this,
    // the obstructions reducer (keyed by roofId) leaks the prior roof's
    // obstructions onto the next project's same-id roof, and any field the next
    // project doesn't carry persists in projectEnvConfig (e.g. setback_distance).
    this.props.dispatch(CLEAR_OBSTRUCTIONS());
    this.props.dispatch(CLEAR_PROJECT_CONFIGURATION());
  }

  maybeAutoSave = (opts: { force?: boolean } = {}) => {
    const { liteUpdating, liteDirty, match, dispatch } = this.props;
    const { projectId } = match.params;
    if (!projectId) return;
    if (liteUpdating) return;
    if (this.state.phase !== 'ready') return;
    // Skip the PUT entirely when nothing has changed since the last save.
    // liteDirty is flipped on by liteDirtyMiddleware for any whitelisted user
    // action and cleared by appReducer on UPDATE_LITE_PROJECT_REQUEST (so a
    // mutation during the in-flight save re-dirties for the next tick) and
    // re-marked on UPDATE_LITE_PROJECT_FAILURE so failures naturally retry.
    if (!liteDirty) return;
    // Idle gate: skip the tick when the user clicked within IDLE_MS. The
    // synchronous savePanelsEditor + deferred RECHECK_PANELS_EXPO bursts
    // monopolise the main thread for hundreds of ms; without this gate,
    // mid-rapid-add clicks land inside the burst and dispatch late. liteDirty
    // stays true through the skip, so the next eligible tick — or the
    // unmount save below (force=true bypasses the gate) — still persists.
    const IDLE_MS = 300;
    const idleRemainingMs = IDLE_MS - (Date.now() - this.lastInteractionAt);
    if (!opts.force && idleRemainingMs > 0) {
      this.scheduleAutoSave(idleRemainingMs);
      return;
    }
    // Flush in-editor panels into drawingManager.roofs[selectedRoofId].panels
    // and fire the PUT. Runs only AFTER exposure recalculation completes so the
    // persisted panels carry the freshly computed exposure, not stale values.
    // Legacy save flow runs savePanelsEditor before serializing; Lite never
    // did, so the PUT body's `roofs` array stayed sparse until unmount.
    const doSave = () => {
      if (this.autoSaveTimeout) {
        clearTimeout(this.autoSaveTimeout);
        this.autoSaveTimeout = null;
      }
      try { savePanelsEditor(); } catch (e) { /* early-tick guard */ }
      // Match Live BOM behavior: persisted design/layout changes must be
      // followed by a BOM invalidation/refetch. Lite's update epic folds that
      // into the PUT via ?regen_bom=true, then chains FETCH_LITE_BOM_REQUEST.
      dispatch(UPDATE_LITE_PROJECT_REQUEST({ projectId, regenBom: true }));
    };
    // Recheck panel exposure zones before saving. launchRecheckPanelExposure
    // hashes the panels array and short-circuits if nothing changed, so
    // repeated calls on the 5s tick are cheap. In original UBuilder this
    // runs when the user leaves a roof; Lite stays in the editor, so we
    // trigger it here instead. The recheck is asynchronous (deferred via
    // setTimeout), so we hand it doSave as a completion callback and only
    // persist once exposure recalculation has finished — otherwise the PUT
    // races ahead and saves stale exposure. The callback fires synchronously
    // when there is nothing to recalculate, keeping the no-op tick cheap.
    let scheduledSave = false;
    try {
      const s = getState() as any;
      const selectedRoofId = s && s.background && s.background.selectedRoofId;
      const panelIds = (s && s.panels && s.panels.panels || []).map((p: any) => p.id);
      if (panelIds.length > 0 && selectedRoofId !== null && selectedRoofId !== undefined) {
        launchRecheckPanelExposure(panelIds, selectedRoofId, doSave, false, LITE_EXPOSURE_DEFER_MS);
        scheduledSave = true;
      }
    } catch (e) { /* early-tick guard */ }
    if (!scheduledSave) {
      doSave();
    }
  }

  render() {
    const { match, projectName, user, dispatch } = this.props;
    const { productId, projectId } = match.params;
    const { phase } = this.state;

    if (phase !== 'ready') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
          <div>Loading panel editor…</div>
        </div>
      );
    }

    return (
      <LitePanelsEditorBoundary>
        <DesignWorkspace
          productId={productId}
          projectId={projectId}
          projectName={projectName}
          user={user}
          dispatch={dispatch}
          editorReady={true}
        />
      </LitePanelsEditorBoundary>
    );
  }
}

function mapStateToProps(appState: any) {
  const liteProject = appState.liteProject || {};
  return {
    projectName: (appState.projectConfiguration && appState.projectConfiguration.projectName) || '',
    user: appState.user || {},
    modelData: appState.moduleSelector ? appState.moduleSelector.modelData : {},
    cords: appState.background ? appState.background.cords : null,
    saveLoadProject: appState.saveLoadProject || { dataToLoad: {}, isLoading: false },
    isLoading: appState.saveLoadProject ? appState.saveLoadProject.isLoading : false,
    liteUpdating: !!liteProject.updating,
    liteDirty: !!(appState.app && appState.app.liteDirty),
  };
}

export default connect(mapStateToProps)(UBuilderLiteContainer);
